import React, { useState, useEffect, useRef, useCallback } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playCorrect, playWrong, playVictory } from '../sounds';

const ACCENT = '#00B4D8';
const ACCENT_LIGHT = 'rgba(0,180,216,0.12)';
const MUTED = '#c4b5d9';

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>{seconds}</div>
    </div>
  );
}

function DrawingCanvas({ onSubmit }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const lastPoint = useRef(null);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPoint.current = getPos(e);
  }, [getPos]);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas || !lastPoint.current) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPoint.current = pos;
  }, [isDrawing, getPos]);

  const endDraw = useCallback(() => {
    setIsDrawing(false);
    lastPoint.current = null;
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    clearCanvas();
  }, []);

  const submitDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL('image/png', 0.5);
    onSubmit(data);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={300}
        height={300}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        style={{
          width: '100%', maxWidth: '300px', height: 'auto', aspectRatio: '1',
          borderRadius: '14px', border: `2px solid ${ACCENT}40`,
          display: 'block', margin: '0 auto 12px', touchAction: 'none',
          background: '#1a1a2e',
        }}
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn btn-ghost" onClick={clearCanvas} style={{ flex: 1 }}>🗑️ נקה</button>
        <button className="btn btn-primary" onClick={submitDrawing}
          style={{ flex: 2, background: ACCENT, borderColor: ACCENT }}>
          שלח ציור ▶
        </button>
      </div>
    </div>
  );
}

function WordGuess({ gameState, playerId, sendMessage, error, clearError }) {
  const {
    phase, currentRound, totalRounds, phaseTimeLeft,
    giverName, giverId, isGiver, word, clueType,
    clues, drawing, hasGuessed, guessedCount, attemptsLeft,
    totalPlayers, guessResults, players, gameOver,
  } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);
  const [clueInputs, setClueInputs] = useState(['', '', '']);
  const [guessInput, setGuessInput] = useState('');
  const [guessResult, setGuessResult] = useState(null);
  const [revealedClueCount, setRevealedClueCount] = useState(1);
  const guessRef = useRef(null);
  const isHost = players?.find(p => p.id === playerId)?.isHost;

  useEffect(() => {
    setLocalTimer(phaseTimeLeft || 0);
  }, [phaseTimeLeft, phase]);

  useEffect(() => {
    if (phase === 'results') return;
    const interval = setInterval(() => {
      setLocalTimer(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    setClueInputs(['', '', '']);
    setGuessInput('');
    setGuessResult(null);
    setRevealedClueCount(1);
    if (phase === 'reveal') {
      // Check my result
      if (guessResults && guessResults[playerId]) {
        if (guessResults[playerId].correct) playCorrect();
        else playWrong();
      }
    } else if (phase === 'results') {
      playVictory();
    } else if (phase === 'guess') {
      if (!isGiver) setTimeout(() => guessRef.current?.focus(), 200);
      playPhaseStart();
    } else {
      playPhaseStart();
    }
  }, [phase, currentRound]);

  // Track wrong guess feedback
  useEffect(() => {
    if (phase === 'guess' && attemptsLeft !== undefined && attemptsLeft < 3 && !hasGuessed) {
      setGuessResult(false);
      playWrong();
    }
  }, [attemptsLeft]);

  // ===== CHOOSE PHASE =====
  if (phase === 'choose') {
    if (isGiver) {
      return (
        <div className="card" style={{ direction: 'rtl' }}>
          <Timer seconds={localTimer} warning={localTimer <= 3} />

          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <p style={{ fontSize: '0.8rem', color: MUTED }}>סיבוב {currentRound} מתוך {totalRounds}</p>
          </div>

          <h2 style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '8px' }}>
            המילה שלך:
          </h2>
          <div style={{
            textAlign: 'center', padding: '20px', marginBottom: '20px',
            background: ACCENT_LIGHT, borderRadius: '16px', border: `2px solid ${ACCENT}`,
          }}>
            <p style={{ fontSize: '2rem', fontWeight: 900, color: ACCENT }}>{word}</p>
          </div>

          <h3 style={{ textAlign: 'center', fontSize: '1rem', marginBottom: '12px', color: MUTED }}>
            איך תרצה לתת רמזים?
          </h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            <button className="btn btn-primary" onClick={() => { playClick(); sendMessage('WORDGUESS_CHOOSE', { type: 'write' }); }}
              style={{ background: '#7C4DFF', borderColor: '#7C4DFF' }}>
              ✍️ כתיבת רמזים
            </button>
            <button className="btn btn-primary" onClick={() => { playClick(); sendMessage('WORDGUESS_CHOOSE', { type: 'draw' }); }}
              style={{ background: ACCENT, borderColor: ACCENT }}>
              🎨 ציור
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 3} />
        <div style={{ textAlign: 'center', padding: '30px 20px' }}>
          <p style={{ fontSize: '0.8rem', color: MUTED, marginBottom: '12px' }}>סיבוב {currentRound} מתוך {totalRounds}</p>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🤔</div>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '8px' }}>
            <span style={{ color: ACCENT }}>{giverName}</span> בוחר/ת איך לתת רמזים...
          </h2>
        </div>
      </div>
    );
  }

  // ===== CLUE PHASE =====
  if (phase === 'clue') {
    if (isGiver) {
      return (
        <div className="card" style={{ direction: 'rtl' }}>
          <Timer seconds={localTimer} warning={localTimer <= 5} />

          <div style={{
            textAlign: 'center', padding: '12px', marginBottom: '16px',
            background: ACCENT_LIGHT, borderRadius: '12px',
          }}>
            <p style={{ fontSize: '0.85rem', color: MUTED }}>המילה:</p>
            <p style={{ fontSize: '1.5rem', fontWeight: 900, color: ACCENT }}>{word}</p>
          </div>

          {clueType === 'write' ? (
            <>
              <h3 style={{ textAlign: 'center', fontSize: '1rem', marginBottom: '12px' }}>
                כתבו עד 3 רמזים (בלי המילה עצמה!)
              </h3>
              {clueInputs.map((c, i) => (
                <input
                  key={i}
                  className="input"
                  placeholder={`רמז ${i + 1}`}
                  value={c}
                  onChange={e => {
                    const next = [...clueInputs];
                    next[i] = e.target.value;
                    setClueInputs(next);
                  }}
                  maxLength={80}
                  autoFocus={i === 0}
                />
              ))}
              <button className="btn btn-primary" onClick={() => {
                playClick();
                sendMessage('WORDGUESS_CLUES', { clues: clueInputs.filter(Boolean) });
              }} disabled={clueInputs.every(c => !c.trim())}
                style={{ background: ACCENT, borderColor: ACCENT }}>
                שלח רמזים ▶
              </button>
            </>
          ) : (
            <>
              <h3 style={{ textAlign: 'center', fontSize: '1rem', marginBottom: '12px' }}>
                ציירו את המילה! 🎨
              </h3>
              <DrawingCanvas onSubmit={(data) => {
                playClick();
                sendMessage('WORDGUESS_DRAWING', { imageData: data });
              }} />
            </>
          )}

          {error && <p className="error-msg">{error}</p>}
        </div>
      );
    }

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />
        <div style={{ textAlign: 'center', padding: '30px 20px' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>
            {clueType === 'draw' ? '🎨' : '✍️'}
          </div>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '8px' }}>
            <span style={{ color: ACCENT }}>{giverName}</span> {clueType === 'draw' ? 'מצייר/ת' : 'כותב/ת רמזים'}...
          </h2>
          <p style={{ color: MUTED, fontSize: '0.85rem' }}>תכף תצטרכו לנחש!</p>
        </div>
      </div>
    );
  }

  // ===== GUESS PHASE =====
  if (phase === 'guess') {
    if (isGiver) {
      return (
        <div className="card" style={{ direction: 'rtl' }}>
          <Timer seconds={localTimer} warning={localTimer <= 5} />
          <h2 style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '16px' }}>
            השחקנים מנחשים... 🤔
          </h2>
          <div style={{
            textAlign: 'center', padding: '16px', marginBottom: '16px',
            background: ACCENT_LIGHT, borderRadius: '14px',
          }}>
            <p style={{ fontSize: '1.5rem', fontWeight: 900, color: ACCENT }}>{word}</p>
          </div>
          <p className="waiting">{guessedCount || 0} מתוך {totalPlayers - 1} ניחשו</p>
        </div>
      );
    }

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '16px' }}>
          מה המילה? 🤔
        </h2>

        {/* Show clues or drawing */}
        {clueType === 'draw' && drawing && (
          <img src={drawing} alt="רמז מצויר" style={{
            width: '100%', maxWidth: '300px', borderRadius: '14px',
            display: 'block', margin: '0 auto 16px',
            border: `2px solid ${ACCENT}40`,
          }} />
        )}
        {clueType === 'write' && clues && clues.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            {clues.slice(0, revealedClueCount).map((c, i) => (
              <div key={i} style={{
                padding: '12px 16px', borderRadius: '12px', marginBottom: '8px',
                background: 'rgba(255,255,255,0.04)', borderRight: `4px solid ${ACCENT}`,
                fontSize: '1.05rem', fontWeight: 600,
                animation: 'fadeIn 0.3s ease-in',
              }}>
                💡 {c}
              </div>
            ))}
            {revealedClueCount < clues.length && (
              <button className="btn btn-ghost" onClick={() => { playClick(); setRevealedClueCount(prev => prev + 1); }}
                style={{ width: '100%', border: `1px dashed ${ACCENT}50`, color: ACCENT, marginTop: '4px' }}>
                🔍 הצג רמז נוסף ({revealedClueCount}/{clues.length})
              </button>
            )}
          </div>
        )}

        {!hasGuessed && (attemptsLeft === undefined || attemptsLeft > 0) ? (
          <>
            {guessResult === false && (
              <div style={{
                textAlign: 'center', padding: '10px', marginBottom: '8px',
                background: 'rgba(255,76,107,0.1)', borderRadius: '12px',
                border: '1px solid rgba(255,76,107,0.2)',
              }}>
                <p style={{ color: '#FF4C6B', fontWeight: 700, fontSize: '0.9rem' }}>
                  ❌ לא נכון! נשארו {attemptsLeft} ניסיונות
                </p>
              </div>
            )}
            <p style={{ textAlign: 'center', color: MUTED, fontSize: '0.8rem', marginBottom: '8px' }}>
              ניסיון {(attemptsLeft !== undefined ? 3 - attemptsLeft : 0) + 1} מתוך 3
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                ref={guessRef}
                className="input"
                placeholder="כתבו את הניחוש שלכם..."
                value={guessInput}
                onChange={e => setGuessInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && guessInput.trim()) {
                    playClick();
                    setGuessInput('');
                    sendMessage('WORDGUESS_GUESS', { guess: guessInput.trim() });
                  }
                }}
                maxLength={50}
                style={{ flex: 1 }}
              />
            </div>
            <button className="btn btn-primary" onClick={() => {
              if (guessInput.trim()) {
                playClick();
                const g = guessInput.trim();
                setGuessInput('');
                sendMessage('WORDGUESS_GUESS', { guess: g });
              }
            }} disabled={!guessInput.trim()}
              style={{ background: ACCENT, borderColor: ACCENT }}>
              נחש! 🎯
            </button>
          </>
        ) : (
          <div style={{
            textAlign: 'center', padding: '20px', borderRadius: '14px',
            background: hasGuessed ? 'rgba(76,175,80,0.1)' : 'rgba(255,76,107,0.1)',
          }}>
            <p style={{ color: hasGuessed ? '#4CAF50' : '#FF4C6B', fontWeight: 700 }}>
              {hasGuessed ? '✓ הניחוש נשלח!' : '❌ נגמרו הניסיונות'}
            </p>
            <p style={{ color: MUTED, fontSize: '0.85rem', marginTop: '6px' }}>{guessedCount || 0} מתוך {totalPlayers - 1} סיימו</p>
          </div>
        )}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== REVEAL PHASE =====
  if (phase === 'reveal') {
    const results = guessResults ? Object.entries(guessResults) : [];

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '16px' }}>
          התשובה! 🎉
        </h2>

        <div style={{
          textAlign: 'center', padding: '20px', marginBottom: '20px',
          background: ACCENT_LIGHT, borderRadius: '16px', border: `2px solid ${ACCENT}`,
        }}>
          <p style={{ fontSize: '2rem', fontWeight: 900, color: ACCENT }}>{word}</p>
          <p style={{ fontSize: '0.85rem', color: MUTED, marginTop: '4px' }}>הרמזים של {giverName}</p>
        </div>

        {/* Show clues/drawing */}
        {clueType === 'draw' && drawing && (
          <img src={drawing} alt="ציור" style={{
            width: '100%', maxWidth: '250px', borderRadius: '14px',
            display: 'block', margin: '0 auto 16px', border: `2px solid ${ACCENT}40`,
          }} />
        )}
        {clueType === 'write' && clues && clues.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            {clues.map((c, i) => (
              <div key={i} style={{
                padding: '8px 12px', borderRadius: '10px', marginBottom: '4px',
                background: 'rgba(255,255,255,0.04)', fontSize: '0.9rem',
              }}>💡 {c}</div>
            ))}
          </div>
        )}

        {/* Guess results */}
        <div style={{ display: 'grid', gap: '8px', marginBottom: '20px' }}>
          {results.map(([pid, r]) => (
            <div key={pid} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', borderRadius: '14px',
              background: r.correct ? 'rgba(76,175,80,0.1)' : 'rgba(255,76,107,0.08)',
              border: r.correct ? '1px solid rgba(76,175,80,0.25)' : '1px solid rgba(255,76,107,0.15)',
            }}>
              <span>
                <span style={{ fontWeight: 700 }}>{r.name}</span>
                <span style={{ color: MUTED, marginRight: '8px', fontSize: '0.85rem' }}>"{r.text}"</span>
              </span>
              <span style={{ fontSize: '1.2rem' }}>{r.correct ? '✅' : '❌'}</span>
            </div>
          ))}
        </div>

        {isHost && !gameOver && (
          <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}
            style={{ width: '100%', background: ACCENT, borderColor: ACCENT }}>
            סיבוב הבא ▶
          </button>
        )}
        {!isHost && !gameOver && <p className="waiting">ממתינים לסיבוב הבא...</p>}

        {gameOver && (
          <>
            {/* Final leaderboard */}
            <div className="result-section" style={{ marginBottom: '20px' }}>
              <h3>טבלת ניקוד סופית</h3>
              <div className="leaderboard">
                {[...(players || [])].sort((a, b) => b.score - a.score).map((p, i) => (
                  <div key={p.id} className="leaderboard-item">
                    <span className="leaderboard-rank">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                    </span>
                    <span className="leaderboard-name">
                      {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>} {p.name}
                      {p.id === playerId && <span className="me-badge"> (אני)</span>}
                    </span>
                    <span className="leaderboard-score">{p.score}</span>
                  </div>
                ))}
              </div>
            </div>

            {isHost && (
              <>
                <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('START_GAME', {}); }}
                  style={{ width: '100%', marginBottom: '8px' }}>שחקו שוב! 🔄</button>
                <button className="btn btn-secondary" onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}
                  style={{ width: '100%' }}>חזרה ללובי</button>
              </>
            )}
            {!isHost && <p className="waiting">ממתינים שהמנהל ימשיך...</p>}
          </>
        )}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // Results / loading
  if (phase === 'results') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '20px' }}>🏆 תוצאות סופיות!</h2>
        <div className="leaderboard">
          {[...(players || [])].sort((a, b) => b.score - a.score).map((p, i) => (
            <div key={p.id} className="leaderboard-item">
              <span className="leaderboard-rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`}</span>
              <span className="leaderboard-name">{p.name}{p.id === playerId && <span className="me-badge"> (אני)</span>}</span>
              <span className="leaderboard-score">{p.score}</span>
            </div>
          ))}
        </div>
        {isHost && (
          <>
            <button className="btn btn-primary" onClick={() => sendMessage('START_GAME', {})} style={{ width: '100%', marginTop: '16px', marginBottom: '8px' }}>שחקו שוב!</button>
            <button className="btn btn-secondary" onClick={() => sendMessage('NEW_GAME', {})} style={{ width: '100%' }}>חזרה ללובי</button>
          </>
        )}
      </div>
    );
  }

  return <div className="card"><p style={{ textAlign: 'center', color: MUTED }}>טוען...</p></div>;
}

export default WordGuess;
